import { useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import quotation from 'src/repositories/quotation';
import { API_KEY } from 'src/constants/apiKey';
import { productFormat } from 'src/utils/product';
import {
  MACHINES_TO_SORT_SIZE,
  STATUS_CODE,
  MACHINING_METHOD,
} from 'src/constants/app';
import { t } from 'src/libs/i18n';
import _ from 'lodash';
import {
  getSizesWithOriginalValue,
  getUniqueSortedItemsByConvertedValue,
  sortSizesByValue,
  getSnpoBunsuHyojiFlags,
} from 'src/utils/helper';

const useManufacturingSize = (
  productCd,
  machiningMethod,
  setEstimateErrors,
  setStockError,
  isProductFinished,
  setDecimalInputSizeFlags,
) => {
  const productSplit = productFormat.splitProductId(productCd);

  const { data, ...rest } = useQuery<
    AxiosResponse<Response.ManufacturingSize>,
    AxiosError<any>
  >(
    [
      API_KEY.MANUFACTURING_SIZES,
      {
        hakudoShhnCd: productSplit?.hakudoShhnCd,
        shhnCd: productSplit?.shhnCd,
        kakoCd: machiningMethod,
        api1ShhnCd: productSplit?.api1ShhnCd,
        api2ShhnCd: productSplit?.api2ShhnCd,
      },
    ],
    quotation.getManufacturingSize,
    {
      retry: 0,
      cacheTime: 0,
      enabled:
        !isProductFinished &&
        Boolean(
          productSplit?.hakudoShhnCd ||
            productSplit?.shhnCd ||
            productSplit?.api1ShhnCd ||
            productSplit?.api2ShhnCd,
        ),
      onSuccess(res) {
        setEstimateErrors([]);
        setStockError('');
        const resData = res?.data?.item;
        const snpoFlags = getSnpoBunsuHyojiFlags(
          machiningMethod === MACHINING_METHOD.KP
            ? null
            : resData?.snpoBunsuHyojis,
          resData?.snpoSize,
        );
        setDecimalInputSizeFlags(snpoFlags);
      },
      onError(err) {
        const error = _.get(err, 'response.data');
        if (STATUS_CODE.GET_SIZES_ERROR === error?.code) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return setEstimateErrors(
            !isProductFinished ? [t('message.E000081')] : [],
          );
        }
      },
    },
  );

  const sizesInfo = (data?.data?.item?.sizesInfo || []).map((item) => ({
    ...item,
    convertedValue: item.sizeS1,
  }));

  let sizes = getSizesWithOriginalValue(
    getUniqueSortedItemsByConvertedValue(sizesInfo),
    true,
  );

  if (MACHINES_TO_SORT_SIZE.includes(machiningMethod)) {
    sizes = sortSizesByValue(sizes) as {
      label: string;
      value: string;
      key: string;
      originalValue: string;
      processingRange?: Response.ProcessingRange | null;
    }[];
  }

  return {
    manufacturingSizesData: { ...data?.data?.item, sizes },
    ...rest,
  };
};

export default useManufacturingSize;
